import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { memberDataQualityQueryOptions } from '@/api/qualityQueryOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import { useColumnState } from '@/components/hooks/useColumnState'
import useGridPageSize from '@/components/hooks/useGridPageSize'
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import { Box } from '@mui/material'
import {
    GridColDef,
    GridColumnVisibilityModel,
} from '@mui/x-data-grid'
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium'
import { t } from 'i18next'
import React from 'react'

export const Route = createFileRoute('/_auth/qualityMember')({
    component: QualityCheckComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function QualityCheckComponent() {
    const memberDataQualityQuery = useSuspenseQuery(
        memberDataQualityQueryOptions(),
    )
    const memberDataQuality = memberDataQualityQuery.data

    const organizations = [
        ...new Set(memberDataQuality.map((qua) => qua.organization.name || '')),
    ].sort()

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    if (xsmall || small || medium) {
        columns.push({
            field: 'member',
            headerName: 'Member',
            valueGetter: (_value, row) =>
                `${row.scoutId} - ${row.firstname} ${row.lastname}`,
            renderCell: (params) => {
                return (
                    <span>
                        <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.member.uuid }}>
                            {params.row.scoutId} - {params.row.firstname} {params.row.lastname}
                        </Link>
                    </span>
                )
            },
        })
    } else {
        columns.push(
            {
                field: 'scoutId',
                headerName: 'Scout-ID',
                valueGetter: (_value, row) => `${row.scoutId}`,
                renderCell: (params) => {
                    return (
                        <span>
                            <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.member.uuid }}>
                                {params.row.scoutId}
                            </Link>
                        </span>
                    )
                },
            },
            {
                field: 'firstname',
                headerName: 'Vorname',
                valueGetter: (_value, row) => `${row.firstname}`,
            },
            {
                field: 'lastname',
                headerName: 'Nachname',
                valueGetter: (_value, row) => `${row.lastname}`,
            },
        )
    }
    columns.push(
        {
            field: 'organization',
            headerName: t('property.membership.organization'),
            type: 'singleSelect',
            valueOptions: organizations,
            valueGetter: (_value, row) => `${row.organization.name}`,
        },
        {
            field: 'message',
            headerName: t('property.quality.message'),
            valueGetter: (_value, row) => `${row.message}`,
        },
        {
            field: 'messageType',
            headerName: t('property.quality.messageType'),
            valueGetter: (_value, row) => {
                return t('enum.qualityError.' + row.messageType)
            },
            renderCell: (params) => {
                return (
                    <span
                        style={{
                            color: params.row.messageType === 'ERROR' ? 'red' : 'orange',
                        }}
                    >
                        {params.value}
                    </span>
                )
            },
        },
    )

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
        organizations: large || xlarge,
        message: true,
        messageType: large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('qualityMember', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('qualityMember', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('qualityMember', 35));

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('qualityMember', apiRef, columns)

    const refreshData = () => {
        memberDataQualityQuery.refetch()
    }

    if (
        memberDataQualityQuery.isLoading ||
        memberDataQualityQuery.isFetching ||
        memberDataQualityQuery.isPending ||
        !memberDataQuality
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <DataGridPremium
                columns={columnsState.columns}
                onColumnWidthChange={columnsState.onColumnWidthChange}
                onColumnOrderChange={columnsState.onColumnOrderChange}
                rows={memberDataQuality}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                pagination
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model)
                    gridPageSize.handlePaginationModelChange('qualityMember', model)
                }}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="qualityMember" refresh={refreshData} /> } }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
                showToolbar />
        </Box>
    );
}
